/**
 * 队员地图点：点击操作弹窗 / 悬停详情弹窗
 */
import mapboxgl from 'mapbox-gl';
import { copyToClipboard } from './clipboardHelpers';
import { isZhxtAppMember, getMemberSourceLabel } from './memberMarkers';
import { getMemberDisplayName } from './deviceIdentity';

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCoordNum(value, digits = 6) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(digits);
}

function formatNum(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number.isInteger(n) ? String(n) : n.toFixed(digits);
}

function buildCopyCoordText(member) {
  const lat = formatCoordNum(member?.last_latitude ?? member?.latitude);
  const lng = formatCoordNum(member?.last_longitude ?? member?.longitude);
  if (lat == null || lng == null) return '';
  return `${lat}, ${lng}`;
}

function resolveDeviceLabel(member) {
  const brand = String(member?.device_brand || member?.brand || member?.manufacturer || '').trim();
  const model = String(member?.device_model || member?.model || member?.phone_model || '').trim();
  if (brand && model) {
    if (model.toLowerCase().includes(brand.toLowerCase())) return model;
    return `${brand} ${model}`;
  }
  return brand || model || '';
}

/** 点击：仅复制坐标 / 删除点位；轻量 App 额外提供更醒目的「指挥对话」 */
export function buildMemberActionPopupHtml(member) {
  const name = escapeHtml(getMemberDisplayName(member) || '队员');
  const copyText = escapeHtml(buildCopyCoordText(member));
  const memberId = escapeHtml(member?.device_unique_id || member?.name || '');
  return `
    <div class="zhxt-member-action-box" data-zhxt-member-id="${memberId}" style="min-width:0">
      <div style="font-weight:600;font-size:11px;margin-bottom:5px;line-height:1.25">${name}</div>
      <button type="button" data-zhxt-member-chat="${memberId}"
        style="display:flex;align-items:center;justify-content:center;gap:5px;width:100%;box-sizing:border-box;padding:5px 8px;margin-bottom:5px;font-size:12px;font-weight:600;line-height:1.2;border:none;background:#0f766e;color:#fff;border-radius:4px;cursor:pointer">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" style="flex:0 0 auto">
          <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H10l-4.2 3.2c-.5.4-1.3 0-1.3-.6V5.5z" fill="currentColor"/>
        </svg>
        <span>指挥对话</span>
      </button>
      <div style="display:flex;gap:4px">
        <button type="button" data-zhxt-member-copy="${copyText}"
          style="flex:1;padding:2px 4px;font-size:10px;line-height:1.3;border:1px solid #cbd5e1;background:#fff;color:#475569;border-radius:3px;cursor:pointer;white-space:nowrap">
          复制坐标
        </button>
        <button type="button" data-zhxt-member-delete="${memberId}"
          style="flex:1;padding:2px 4px;font-size:10px;line-height:1.3;border:1px solid #fecaca;background:#fff;color:#b91c1c;border-radius:3px;cursor:pointer;white-space:nowrap">
          删除点位
        </button>
      </div>
    </div>
  `;
}

/** 悬停：队员设备令牌详情 */
export function buildMemberHoverPopupHtml(member) {
  const lat = formatCoordNum(member?.last_latitude);
  const lng = formatCoordNum(member?.last_longitude);
  const alt = formatNum(member?.last_altitude, 1);
  const acc = formatNum(member?.last_accuracy, 1);
  const speed = formatNum(member?.last_speed, 1);
  const bearing = formatNum(member?.last_bearing, 1);
  const device = resolveDeviceLabel(member);
  const gsm = member?.gsm != null && member.gsm !== '' ? String(member.gsm) : '未知';
  const battery = member?.battery_level != null && member.battery_level !== ''
    ? `${member.battery_level}%`
    : '未知';
  const seen = member?.last_seen ? new Date(member.last_seen).toLocaleString() : '未知';

  return `
    <div style="max-width:220px;font-size:11px;line-height:1.35;color:#222">
      <div style="font-weight:700;margin-bottom:3px">${escapeHtml(member?.name || '队员')}
        <span style="font-weight:500;color:#64748b"> · ${escapeHtml(getMemberSourceLabel(member))}</span>
      </div>
      <div style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:10px;color:#334155">
        <div>经度: ${lng != null ? escapeHtml(lng) : '未知'}</div>
        <div>纬度: ${lat != null ? escapeHtml(lat) : '未知'}</div>
        <div>海拔: ${alt != null ? `${escapeHtml(alt)} m` : '未知'}</div>
        <div>精度: ${acc != null ? `${escapeHtml(acc)} m` : '未知'}</div>
        <div>速度: ${speed != null ? `${escapeHtml(speed)} m/s` : '未知'}</div>
        <div>方向: ${bearing != null ? `${escapeHtml(bearing)}°` : '未知'}</div>
        <div>电量: ${escapeHtml(battery)}</div>
        <div>信号: ${escapeHtml(gsm)}</div>
        <div>手机: ${device ? escapeHtml(device) : '未知'}</div>
        <div style="color:#64748b;margin-top:2px">上报: ${escapeHtml(seen)}</div>
      </div>
    </div>
  `;
}

/**
 * 为队员标记绑定：悬停详情 + 点击操作（复制/删除[/对话]）
 */
export function bindMemberMarkerInteractions(map, marker, el, member, displayLngLat, options = {}) {
  if (!map || !marker || !el || !member || !displayLngLat) return;

  const {
    onFlyTo,
    onRemoveFromMap,
    onOpenChat
  } = options;

  // 避免浏览器原生 title 与悬停弹层叠在一起
  el.removeAttribute('title');
  el.title = '';

  const hoverPopup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 32,
    maxWidth: '240px',
    className: 'zhxt-member-hover-popup',
    anchor: 'bottom'
  }).setHTML(buildMemberHoverPopupHtml(member));

  const actionPopup = new mapboxgl.Popup({
    closeButton: true,
    offset: 28,
    maxWidth: '220px',
    className: 'zhxt-member-action-popup',
    anchor: 'bottom'
  }).setHTML(buildMemberActionPopupHtml(member));

  let hoverTimer = null;

  const closeHover = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
    try { hoverPopup.remove(); } catch (_) { /* ignore */ }
  };

  const openHover = () => {
    if (actionPopup.isOpen()) return;
    closeHover();
    hoverPopup.setLngLat(displayLngLat).addTo(map);
  };

  const onActionClick = (event) => {
    const copyBtn = event.target?.closest?.('[data-zhxt-member-copy]');
    if (copyBtn) {
      event.preventDefault();
      event.stopPropagation();
      const text = copyBtn.getAttribute('data-zhxt-member-copy') || buildCopyCoordText(member);
      if (!text) return;
      const original = copyBtn.textContent;
      copyToClipboard(text, { successMessage: '', failMessage: '复制失败，请手动复制坐标' });
      copyBtn.textContent = '已复制';
      setTimeout(() => {
        if (copyBtn.isConnected) copyBtn.textContent = original || '复制坐标';
      }, 1000);
      return;
    }

    const delBtn = event.target?.closest?.('[data-zhxt-member-delete]');
    if (delBtn) {
      event.preventDefault();
      event.stopPropagation();
      const ok = window.confirm('从地图移除此点位？可在左侧队员列表中重新「显示」。');
      if (!ok) return;
      try { actionPopup.remove(); } catch (_) { /* ignore */ }
      onRemoveFromMap?.(member);
      return;
    }

    const chatBtn = event.target?.closest?.('[data-zhxt-member-chat]');
    if (chatBtn) {
      event.preventDefault();
      event.stopPropagation();
      try { actionPopup.remove(); } catch (_) { /* ignore */ }
      onOpenChat?.(member);
    }
  };

  actionPopup.on('open', () => {
    closeHover();
    const popupEl = actionPopup.getElement();
    popupEl?.addEventListener('click', onActionClick);
  });
  actionPopup.on('close', () => {
    const popupEl = actionPopup.getElement();
    popupEl?.removeEventListener('click', onActionClick);
  });

  el.style.cursor = 'pointer';
  el.addEventListener('mouseenter', () => {
    hoverTimer = setTimeout(openHover, 120);
  });
  el.addEventListener('mouseleave', () => {
    closeHover();
  });

  el.addEventListener('click', (evt) => {
    evt.stopPropagation();
    closeHover();
    onFlyTo?.(member);
    if (actionPopup.isOpen()) {
      actionPopup.remove();
      return;
    }
    actionPopup.setLngLat(displayLngLat).addTo(map);
  });

  // 供清理时关闭弹层
  marker.__zhxtMemberPopups = { hoverPopup, actionPopup, closeHover };
}
